import {
  SET_FILTERS_DATA,
  SET_VIEW_REPORT_AS,
  UPDATE_CURRENT_REPORT,
  UPDATE_CURRENT_REPORT_FILTERS,
  UPDATE_PARTIAL_REPORT_MESSAGE,
  UPDATE_OFFLINE_TIMEOUT_MESSAGE,
  UPDATE_WCF_REQUEST_ID,
  RESET_CURRENT_REPORT,
} from '../mutation-types'
import {
  GET_FILTERS,
  VIEW_REPORT,
  SCHEDULE_REPORT,
  CLOSE_REPORT,
} from '../action-types'

const REPORT_API_STATUS = {
  SUCCESS: 'SUCCESS',
  PARTIAL: 'PARTIAL',
  TIMEOUT: 'TIMEOUT',
  ERROR: 'ERROR',
}

const reports = {
  namespaced: true,
  state: {
    /**
     * aka AgreementStatusCode in DRWIN
     */
    reconciliationStatuses: [
      {
        id: '0',
        display: 'Open',
        default: true,
      },
      {
        id: '1',
        display: 'Reconciled',
        default: false,
      },
      {
        id: '2',
        display: 'All',
        default: false,
      },
    ],
    claimStatuses: [
      {
        id: '1',
        display: 'Filed',
      },
      {
        id: '2',
        display: 'Under Audit',
      },
      {
        id: '3',
        display: 'Received',
      },
      {
        id: '4',
        display: 'Rejected',
      },
      {
        id: '5',
        display: 'Paid',
      },
      {
        id: '6',
        display: 'Cert Issuance',
      },
    ],
    /**
     * per drwin team:
     *
     * eventstatus = 1 active events
     * eventstatus = 0 inactive events
     * eventstatus = 2 all events.
     */
    eventStatuses: [
      {
        id: '1',
        display: 'Active',
        default: true,
      },
      {
        id: '0',
        display: 'Inactive',
        default: false,
      },
      {
        id: '2',
        display: 'All',
        default: false,
      },
    ],
    currentReport: null,
    currentReportFilters: null,
    partialDataMessage: null,
    offlineTimeoutMessage: null,
    wcfRequestId: null,
    filtersData: null,
    viewReportAs: null,
    instructions: {
      claims: `
      <div class="ui mini header">Report Search Requirements</div>
      <div class="ui small relaxed list">
        <div class="item">A Record Date Start and End Date are required. The maximum Record Date range is 5 calendar years*.</div>
        <div class="item"><div>Reports will not include:</div>
          <div class="ui list">
            <div class="item"><i class="right triangle icon"></i>
            <div class="content"><strong>QR/LF</strong>&nbsp;claims&nbsp;that&nbsp;were&nbsp;<strong>Paid</strong>&nbsp;or&nbsp;<strong>Rejected</strong>&nbsp;more&nbsp;than&nbsp;5&nbsp;years&nbsp;ago.</div></div>
            <div class="item"><i class="right triangle icon"></i>
            <div class="content"><strong>RAS</strong>&nbsp;claims&nbsp;that&nbsp;were&nbsp;<strong>Paid</strong>&nbsp;or&nbsp;<strong>Rejected</strong>&nbsp;more&nbsp;than&nbsp;1&nbsp;year&nbsp;ago.</div></div>
          </div>
        </div>
      </div>
      <p class="small-text">*  The&nbsp;5-year&nbsp;maximum&nbsp;Record&nbsp;Date&nbsp;range&nbsp;is&nbsp;based&nbsp;on&nbsp;the&nbsp;selected&nbsp;start or end date. For example, if Record Date Start is 1/1/2015, the Maximum Record Date End will be 12/31/2020.</p>`,
      payments: ``,
      events: ``,
    },
  },
  mutations: {
    [SET_FILTERS_DATA](state, payload) {
      state.filtersData = payload
    },
    [SET_VIEW_REPORT_AS](state, payload) {
      state.viewReportAs = payload
    },
    [UPDATE_CURRENT_REPORT](state, payload) {
      state.currentReport = payload
    },
    [UPDATE_CURRENT_REPORT_FILTERS](state, payload) {
      state.currentReportFilters = payload
    },
    [UPDATE_PARTIAL_REPORT_MESSAGE](state, payload) {
      state.partialDataMessage = payload
    },
    [UPDATE_OFFLINE_TIMEOUT_MESSAGE](state, payload) {
      state.offlineTimeoutMessage = payload
    },
    [UPDATE_WCF_REQUEST_ID](state, payload) {
      state.wcfRequestId = payload
    },
    [RESET_CURRENT_REPORT](state) {
      state.currentReport = null
      state.currentReportFilters = null
      state.partialDataMessage = null
      state.offlineTimeoutMessage = null
      state.wcfRequestId = null
    },
  },
  actions: {
    async [GET_FILTERS]({ commit, state }) {
      commit(
        SET_FILTERS_DATA,
        await $.get(
          `${API_URL}/reports/filters${
            state.viewReportAs ? '/' + state.viewReportAs.userId : ''
          }`,
        ),
      )
    },
    async [VIEW_REPORT]({ commit, state }, payload) {
      const report = payload || state.currentReportFilters
      const reportName = report.name.toLowerCase()

      commit(UPDATE_CURRENT_REPORT_FILTERS, report)

      const aryParamsWithData = Object.values(report.model).filter((item) =>
        Array.isArray(item.value) ? item.value.length > 0 : !!item.value,
      )
      const params = {}
      aryParamsWithData.forEach((item) => {
        // Check if value is an array, if so flatten array as comma-separated string
        // for the API to process as single value
        params[item.id] = Array.isArray(item.value)
          ? item.value.join()
          : item.value
      })

      // Add ForUserId parameter if another user is selected in UI
      if (state.viewReportAs) {
        params['forUserId'] = state.viewReportAs.userId
      }
      // for now, we will be passing all params as query string to be parsed by a complex type
      // in the API
      // const query = { q: JSON.stringify(params) }

      await $.get(`${API_URL}/reports/${reportName}`, params).then(
        (response) => {
          commit(UPDATE_WCF_REQUEST_ID, response.wcfRequestId)
          switch (response.status) {
            case REPORT_API_STATUS.SUCCESS:
              commit(UPDATE_CURRENT_REPORT, response.data)
              break
            case REPORT_API_STATUS.PARTIAL:
              commit(UPDATE_PARTIAL_REPORT_MESSAGE, response.message)
              commit(UPDATE_CURRENT_REPORT, response.data)
              break
            case REPORT_API_STATUS.TIMEOUT:
              commit(UPDATE_OFFLINE_TIMEOUT_MESSAGE, response.message)
              break
            case REPORT_API_STATUS.ERROR:
              throw response.message
          }
        },
      )
    },
    async [SCHEDULE_REPORT]({ commit, state }, payload) {
      const report = payload
      const reportName = report.name.toLowerCase()
      const aryParamsWithData = Object.values(report.model).filter((item) =>
        Array.isArray(item.value) ? item.value.length > 0 : !!item.value,
      )
      const params = {}
      aryParamsWithData.forEach((item) => {
        // Check if value is an array, if so flatten array as comma-separated string
        // for the API to process as single value
        params[item.id] = Array.isArray(item.value)
          ? item.value.join()
          : item.value
      })

      // Add ForUserId parameter if another user is selected in UI
      if (state.viewReportAs) {
        params['forUserId'] = state.viewReportAs.userId
      }
      // for now, we will be passing all params as query string to be parsed by a complex type
      // in the API
      // const query = { q: JSON.stringify(params) }
      await $.get(`${API_URL}/reports/${reportName}/schedule`, params).then(
        (response) => {
          commit(UPDATE_WCF_REQUEST_ID, response.wcfRequestId)
          switch (response.status) {
            case REPORT_API_STATUS.SUCCESS:
              // TODO
              break
            case REPORT_API_STATUS.TIMEOUT:
            case REPORT_API_STATUS.ERROR:
              throw response.message
          }
        },
      )
    },
    [CLOSE_REPORT]({ commit }) {
      commit(RESET_CURRENT_REPORT)
    },
  },
  getters: {
    claimTypes(state, getters, rootState) {
      return Object.values(rootState.PROCEDURE_TYPES)
    },
  },
}

export default reports
